import axios from "./axios";

export const NOONLIGHT_ALARMS_PAGE_SIZE = 10;

export type PaginationMeta = {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type NoonlightAlarmRecord = {
  user_id?: number;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  service?: string;
  location?: string;
  zipcode?: string;
  active_status?: string;
  date_of_activation?: string;
  date_of_canellation?: string;
  unique_location_identifier?: string;
  status?: string;
  duration?: string;
};

export type NoonlightAlarmsData = {
  alarms: NoonlightAlarmRecord[];
  total_duration: number;
  pagination?: PaginationMeta;
};

export type NoonlightAlarmsQuery = {
  email?: string;
  featureKey?: string;
  page?: number;
  limit?: number;
};

export const normalizeNoonlightPagination = (
  pagination: Record<string, unknown> | null | undefined,
): PaginationMeta | undefined => {
  if (!pagination) return undefined;
  return {
    total: Number(pagination.totalCount ?? pagination.total ?? 0),
    page: Number(pagination.currentPage ?? pagination.page ?? 1),
    limit: Number(pagination.limit ?? NOONLIGHT_ALARMS_PAGE_SIZE),
    totalPages: Number(pagination.totalPages ?? 1),
  };
};

export const buildNoonlightAlarmsUrl = ({
  email,
  featureKey,
  page = 1,
  limit = NOONLIGHT_ALARMS_PAGE_SIZE,
}: NoonlightAlarmsQuery): string => {
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  if (featureKey) params.set(featureKey, "true");
  params.set("page", String(page));
  params.set("limit", String(limit));
  return `/omw/subadmin/noonlight-alarms?${params.toString()}`;
};

export const fetchNoonlightAlarms = async (
  query: NoonlightAlarmsQuery,
): Promise<NoonlightAlarmsData> => {
  const result = await axios({
    URL: buildNoonlightAlarmsUrl(query),
    method: "GET",
  });
  const data = result?.data?.data ?? result?.data;
  return {
    alarms: data?.alarms ?? [],
    total_duration: data?.total_duration ?? 0,
    pagination: normalizeNoonlightPagination(data?.pagination),
  };
};

export type DashboardFeatureEntry = {
  label: string;
  count: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const coerceFeatureLabel = (
  value: unknown,
  featureKey: string,
  fallbackLabel?: (key: string) => string,
): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (isRecord(value)) {
    if (typeof value.label === "string") return value.label;
    if (typeof value.Label === "string") return value.Label;
    if (isRecord(value.label)) {
      return coerceFeatureLabel(value.label, featureKey, fallbackLabel);
    }
  }
  return fallbackLabel?.(featureKey) ?? featureKey;
};

export const coerceFeatureCount = (value: unknown): number => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (isRecord(value)) {
    if ("count" in value || "Count" in value || "total" in value) {
      return coerceFeatureCount(value.count ?? value.Count ?? value.total);
    }
  }
  return 0;
};

/** Coerce API values that may be numbers or `{ label, count }` objects. */
export const toSafeNumber = (value: unknown, fallback = 0): number => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  if (isRecord(value)) {
    return coerceFeatureCount(value);
  }
  return fallback;
};

/** Supports new `{ label, count }` shape and legacy numeric values. */
export const parseDashboardFeature = (
  featureKey: string,
  value: unknown,
  fallbackLabel?: (key: string) => string,
): DashboardFeatureEntry => {
  if (isRecord(value)) {
    const label = coerceFeatureLabel(
      value.label ?? value.Label,
      featureKey,
      fallbackLabel,
    );
    const rawCount = value.count ?? value.Count ?? value.total ?? value.value;
    if (rawCount !== undefined) {
      return {
        label,
        count: coerceFeatureCount(rawCount),
      };
    }
    return {
      label,
      count: 0,
    };
  }

  return {
    label: fallbackLabel?.(featureKey) ?? featureKey,
    count: coerceFeatureCount(value),
  };
};

export const getDashboardFeatureEntries = (
  features: unknown,
  fallbackLabel?: (key: string) => string,
): Array<{ key: string; label: string; count: number }> => {
  if (!features) return [];

  if (Array.isArray(features)) {
    return features.map((item, index) => {
      const parsed = parseDashboardFeature(String(index), item, fallbackLabel);
      const key =
        isRecord(item) && typeof item.key === "string"
          ? item.key
          : isRecord(item) && typeof item.id === "string"
            ? item.id
            : String(index);
      return { key, ...parsed };
    });
  }

  if (!isRecord(features)) return [];

  return Object.entries(features).map(([featureKey, value]) => ({
    key: featureKey,
    ...parseDashboardFeature(featureKey, value, fallbackLabel),
  }));
};
